import React, { useEffect, useMemo, useState } from 'react';
import { appealsAPI } from './api';
import './gpj-modules.css';

const safeError = (error, fallback) => error.response?.data?.detail || fallback;

function Determiner({ module }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const enabledQuestions = module.questions.filter((question) => question.enabled);
  const complete = enabledQuestions.every(
    (question) => Boolean(answers[question.question_id])
  );

  const choose = (questionId, choiceId) => {
    setAnswers((current) => ({ ...current, [questionId]: choiceId }));
    setResult(null);
    setError('');
  };

  const evaluate = async () => {
    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const response = await appealsAPI.evaluate(module.ruleset_version, answers);
      setResult(response.data);
    } catch (requestError) {
      setError(safeError(requestError, 'Unable to review these appeal factors.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gpj-determiner">
      <div className="gpj-review-status" role="status">
        <strong>Determiner status:</strong>{' '}
        {module.determiner_available
          ? 'controlled local preview — no court route will be returned'
          : 'pending compliance review — public answers are disabled'}
      </div>

      {module.questions.map((question) => (
        <fieldset
          className={`gpj-q${question.enabled ? '' : ' gpj-q--pending'}`}
          key={question.question_id}
          disabled={!module.determiner_available || !question.enabled}
        >
          <legend className="gpj-qlabel">
            {question.order}. {question.wording}
          </legend>
          <p className="gpj-q-context">{question.applicability}</p>
          {question.choices.length > 0 && (
            <div className="gpj-opts">
              {question.choices.map((choice) => (
                <button
                  type="button"
                  key={choice.choice_id}
                  className={`gpj-opt${answers[question.question_id] === choice.choice_id ? ' is-sel' : ''}`}
                  onClick={() => choose(question.question_id, choice.choice_id)}
                  aria-pressed={answers[question.question_id] === choice.choice_id}
                  disabled={!module.determiner_available || !question.enabled}
                >
                  {choice.label}
                </button>
              ))}
            </div>
          )}
          {question.status_note && (
            <p className="gpj-pending-note">{question.status_note}</p>
          )}
        </fieldset>
      ))}

      {module.determiner_available && (
        <button
          type="button"
          className="gpj-btn gpj-btn--teal"
          onClick={evaluate}
          disabled={!complete || submitting}
        >
          {submitting ? 'Reviewing…' : 'Review these factors'}
        </button>
      )}

      {error && <div className="gpj-action-error" role="alert">{error}</div>}
      {result && (
        <div className="gpj-result" aria-live="polite">
          <span className="gpj-result__lead">{result.result.title}</span>
          {result.result.general_information}
          <span className="gpj-result__cite">
            This controlled preview never returns a court route or a personalized deadline.
          </span>
        </div>
      )}
    </div>
  );
}

export default function AppealsModule({ setCurrentView = () => {} }) {
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await appealsAPI.getModule();
        if (active) setModule(response.data);
      } catch (requestError) {
        if (active) {
          setError(safeError(requestError, 'Unable to load the appeals pathway.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const sourceById = useMemo(() => Object.fromEntries(
    (module?.sources || []).map((source) => [source.resource_id, source])
  ), [module]);

  if (loading) {
    return (
      <main className="gpj gpj-loading" aria-live="polite">
        <div className="gpj-wrap">Loading verified appeals information…</div>
      </main>
    );
  }

  if (error || !module) {
    return (
      <main className="gpj gpj-loading" role="alert">
        <div className="gpj-wrap">{error || 'Appeals information is unavailable.'}</div>
      </main>
    );
  }

  return (
    <main className="gpj">
      <section className="gpj-appeals-hero" id="appeals">
        <div className="gpj-wrap">
          <span className="gpj-eyebrow">Appeals · ruleset {module.ruleset_version}</span>
          <h2>{module.title}</h2>
          <p className="gpj-intro">{module.introduction}</p>

          <div className="gpj-warn">
            <span className="gpj-warn__icon" aria-hidden="true">!</span>
            <span className="gpj-warn__txt">
              <b>{module.warning_title}</b>
              {module.warning_text}
            </span>
          </div>
        </div>
      </section>

      <section className="gpj-section">
        <div className="gpj-wrap">
          <div className="gpj-step-block">
            <span className="gpj-step-tag"><span className="gpj-sn">1</span> Review route factors</span>
            <h3>Four-question determiner foundation</h3>
            <p>
              The first three factors are source-backed. The fourth slot remains visible but
              blocked until its answer categories and the full determiner receive review.
            </p>
            <Determiner module={module} />
            <p className="gpj-unresolved-note">{module.unresolved_issue}</p>
          </div>

          <div className="gpj-step-block">
            <span className="gpj-step-tag"><span className="gpj-sn">2</span> Read condition-bound rules</span>
            <h3>What Rule 38 says</h3>
            <p>
              Each rule below is tied to its stated condition and verified source. This is not
              a deadline calculator, and a row should not be applied outside its condition.
            </p>
            <div className="gpj-dl-table">
              {module.deadline_rules.map((deadline) => (
                <div className="gpj-dl-row" key={deadline.rule_id}>
                  <div className="gpj-dl-cond">{deadline.condition}</div>
                  <div className="gpj-dl-rule">
                    {deadline.general_rule_text}
                    <span className="gpj-rule-source">
                      {deadline.source_references.map((reference) => {
                        const source = sourceById[reference.resource_id];
                        return source ? (
                          <a
                            key={reference.resource_id}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {source.title} · v{reference.version}
                          </a>
                        ) : null;
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="gpj-step-block">
            <span className="gpj-step-tag"><span className="gpj-sn">3</span> Open the applicable form source</span>
            <h3>Form {module.form.form_number}: {module.form.title}</h3>
            <p>{module.form_context}</p>
            <div className="gpj-form-actions gpj-form-actions--spaced">
              <a
                className="gpj-a-link gpj-a-link--primary"
                href={module.form.official_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open official Form {module.form.form_number}
                <span className="gpj-ext" aria-hidden="true">↗</span>
              </a>
            </div>
          </div>

          <div className="gpj-step-block">
            <span className="gpj-step-tag"><span className="gpj-sn">4</span> Read sources and find help</span>
            <h3>Official materials and support directory</h3>
            <p>
              Read the current official material and seek advice about how it applies to an
              individual order. GPJ's directory remains a separate contact-search boundary.
            </p>

            <div className="gpj-sources">
              {module.sources.map((source) => (
                <a
                  className="gpj-source"
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={source.resource_id}
                >
                  <span>
                    <span className="gpj-source__title">{source.title}</span>
                    <span className="gpj-source__sub">{source.source_name} · verified source v{source.version}</span>
                  </span>
                  <span className="gpj-source__arrow" aria-hidden="true">↗</span>
                </a>
              ))}
              <button
                type="button"
                className="gpj-source"
                onClick={() => setCurrentView(module.directory.url.slice(1))}
              >
                <span>
                  <span className="gpj-source__title">{module.directory.title}</span>
                  <span className="gpj-source__sub">{module.directory.description}</span>
                </span>
                <span className="gpj-source__arrow" aria-hidden="true">→</span>
              </button>
            </div>

            <p className="gpj-disclaimer"><b>Important boundary.</b> {module.disclaimer}</p>
          </div>
        </div>
      </section>
    </main>
  );
}
